import React, { useState } from "react";
import { formatPrice } from "../utils/formatPrice";
import { trackEvent } from "../utils/analytics";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Turnstile } from "@marsidev/react-turnstile";
import { reserveThisBoat } from "../utils/boatApi";
import { createShareLink } from "../utils/comparatorApi";
import coverPhoto from "../assets/images/landscape-cover.jpg";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

interface ReservationModalProps {
  open: boolean;
  onClose: () => void;
  images: any;
  name: any;
  kind: any;
  formData: any;
  setFormData: any;
  price: any;
  id: any;
  startPrice: any;
  yachtDetails?: any;
  skipper: boolean;
  startBaseId: number;
  endBaseId: number;
  skipperData: any;
}

const ReservationModal: React.FC<ReservationModalProps> = ({
  open,
  onClose,
  images,
  name,
  kind,
  formData,
  setFormData,
  price,
  id,
  startPrice,
  yachtDetails,
  skipper,
  startBaseId,
  endBaseId,
  skipperData,
}) => {
  const { yacht } = useSelector((state: any) => state.yacht);
  const navigate = useNavigate();
  const [isSendingRequest, setIsSendingRequest] = useState<boolean>(false);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [sailingPlanCode, setSailingPlanCode] = useState<string>("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValidationError("");
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const formatToISOString = (dateStr: any) => dateStr.replace(" ", "T");

  const formatDateRange = (checkIn: string, checkOut: string) => {
    if (!checkIn || !checkOut) return "";
    return `${moment(checkIn).format("D/M/YYYY")} - ${moment(checkOut).format("D/M/YYYY")}`;
  };

  const formatDateShort = (dateStr: string) =>
    dateStr ? moment(dateStr).format("D MMM YYYY") : "";

  const handleClose = () => {
    if (reservationSuccess) {
      setFormData({
        checkIn: "",
        checkOut: "",
        name: "",
        surname: "",
        email: "",
        telephone: "",
        notes: "",
      });
      setReservationSuccess(false);
      setSailingPlanCode("");
    }
    setTurnstileToken("");
    onClose();
  };

  const reserveBoat = async () => {
    if (!formData.name || !formData.email) {
      setValidationError("Name and email address are required.");
      return;
    }
    if (!turnstileToken) {
      setValidationError("Please complete the security check.");
      return;
    }
    setIsSendingRequest(true);

    // Create sailing plan share link first so we can include it in the booking email
    let sailingPlanUrl = '';
    let shareCode = '';
    try {
      const baseName = yachtDetails?.startBase?.split('/')?.[0]?.trim() || '';
      const country = yachtDetails?.location?.country || '';
      const result = await createShareLink(
        `${window.location.origin}/yacht-details/${id}`,
        {
          og_title: `${name} ${kind} — SailArmada`,
          og_image: images?.[0]?.url || '',
          og_description: `${formatDateShort(formData.checkIn)} → ${formatDateShort(formData.checkOut)}${baseName ? ' · ' + baseName : ''}${country ? ', ' + country : ''}`,
        },
        'sailing_plan',
        {
          yachtId: String(id),
          yachtName: name,
          kind,
          images: (images || []).slice(0, 8),
          dateFrom: formData.checkIn,
          dateTo: formData.checkOut,
          startBase: yachtDetails?.startBase,
          country,
        },
        formData.checkIn,
      );
      shareCode = result.code;
      sailingPlanUrl = `${window.location.origin}/s/${result.code}`;
    } catch {
      // Share link failure doesn't block the booking
    }

    try {
      const data = {
        date_from: formatToISOString(formData.checkIn),
        date_to: formatToISOString(formData.checkOut),
        yacht_id: id,
        client_name: `${formData.name} ${formData.surname}`.trim(),
        customer_email: formData.email,
        telephone: formData.telephone || "",
        currency: "EUR",
        product_name: yacht?.product || undefined,
        skipper: skipper,
        base_from_id: startBaseId,
        base_to_id: endBaseId,
        notes: formData?.notes || "",
        field_name: skipperData?.name || "",
        turnstile_token: turnstileToken,
        sailing_plan_url: sailingPlanUrl,
      };

      const response = await reserveThisBoat(data);
      if (response?.id) {
        trackEvent('booking_submitted', {
          yacht_id: String(id || ''),
          yacht_name: name || '',
          date_from: formData.checkIn || '',
          date_to: formData.checkOut || '',
        });
        setSailingPlanCode(shareCode);
        setReservationSuccess(true);
      } else {
        setValidationError("Reservation failed. Please try again or contact us at charter@sailarmada.com.");
      }
    } catch {
      setValidationError("Reservation failed. Please try again or contact us at charter@sailarmada.com.");
    } finally {
      setIsSendingRequest(false);
    }
  };

  // ── Success card ──────────────────────────────────────────────────────────
  if (reservationSuccess) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ py: 5, px: 4, textAlign: "center" }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Booking Request Sent!
          </Typography>

          <Box
            sx={{
              background: "#f0f4f8",
              borderRadius: 2,
              px: 3,
              py: 2,
              my: 3,
              textAlign: "left",
            }}
          >
            <Typography variant="body2" fontWeight="bold" noWrap>
              {name} {kind}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {yachtDetails?.startBase?.split("/")?.[0]?.trim()}
              {yachtDetails?.location?.country ? `, ${yachtDetails.location.country}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatDateShort(formData.checkIn)} → {formatDateShort(formData.checkOut)}
              {price ? ` · ${formatPrice(price)}` : ""}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="body2" color="text.secondary" gutterBottom>
            A confirmation email has been sent to <strong>{formData.email}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Our team will contact you within <strong>24 hours</strong> to finalise your booking.
            No payment is due at this stage.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'center', pb: 3, gap: 1 }}>
          {sailingPlanCode && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => { navigate(`/s/${sailingPlanCode}`); handleClose(); }}
            >
              View Sailing Plan →
            </Button>
          )}
          <Button
            size="small"
            sx={{ color: 'text.secondary' }}
            onClick={handleClose}
          >
            {sailingPlanCode ? 'Return to boat' : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Booking form ──────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Make a Reservation</DialogTitle>
      <DialogContent>
        {/* Yacht summary */}
        <Box display="flex" alignItems="center" mb={2}>
          <img
            src={images[0]?.url || coverPhoto}
            alt="Yacht"
            style={{ width: "80px", height: "80px", marginRight: "16px", objectFit: "cover", borderRadius: 4 }}
          />
          <Box>
            <Typography variant="subtitle1">
              {name} {kind}
            </Typography>
            <Typography variant="subtitle1">
              {yachtDetails?.startBase?.split("/")?.[0]?.replace(/\s+$/, "")
                ? `${yachtDetails.startBase.split("/")?.[0].replace(/\s+$/, "")},`
                : ""}
              {yachtDetails?.location?.country && ` ${yachtDetails.location.country}`}
            </Typography>
            <Typography variant="subtitle1">
              {formatDateRange(formData?.checkIn, formData.checkOut)}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {startPrice && startPrice !== price && (
                <span style={{ textDecoration: "line-through", marginRight: "8px" }}>
                  {formatPrice(startPrice)}
                </span>
              )}
              <span style={{ color: "#000", fontWeight: "bold" }}>{formatPrice(price)}</span>{" "}
              {yacht?.obligatoryExtras?.length > 0 ? (
                <span
                  onClick={() => setExtrasOpen(true)}
                  style={{ cursor: "pointer", textDecoration: "underline dotted", color: "#666" }}
                >
                  Excluding mandatory services ℹ️
                </span>
              ) : (
                <>Excluding mandatory services</>
              )}
            </Typography>

            <Dialog open={extrasOpen} onClose={() => setExtrasOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle>Mandatory Services</DialogTitle>
              <DialogContent>
                <List dense>
                  {yacht?.obligatoryExtras?.map((extra: any) => (
                    <ListItem key={extra.id} sx={{ px: 0 }}>
                      <ListItemText primary={extra.name} />
                      <Typography variant="body2">{formatPrice(extra.price, extra.currency || "€")}</Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography fontWeight="bold">Total</Typography>
                  <Typography fontWeight="bold">
                    {formatPrice(yacht?.obligatoryExtras?.reduce((sum: number, e: any) => sum + (e.price || 0), 0))}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setExtrasOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Box>

        <Typography color="success.main" mb={2}>
          ✔️ Confirmed Availability!
        </Typography>
        <Typography variant="body2" mb={1}>
          Please complete the request form below to block this offer now and prevent other customers from booking it.
        </Typography>
        <Typography variant="body2" mb={3}>
          Don't worry: no fee will be charged, and you will have two days to confirm your booking.
        </Typography>

        {/* Form */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Name *"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Surname"
              name="surname"
              value={formData.surname || ""}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              margin="dense"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email Address *"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              margin="dense"
              type="email"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Phone Number"
              name="telephone"
              value={formData.telephone || ""}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              margin="dense"
              type="tel"
              placeholder="+XX XXX XXX XXXX"
              helperText="Include country code, e.g. +30 694 123 4567"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes (Optional)"
              name="notes"
              value={formData.notes || ""}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onExpire={() => setTurnstileToken("")}
            onError={() => setTurnstileToken("")}
            options={{ theme: "light" }}
          />
        </Box>

        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setValidationError("")}>
            {validationError}
          </Alert>
        )}

        <Typography variant="body2" mt={3}>
          By clicking "Send request," you agree with our{" "}
          <a href="https://sailarmada.com/termeni-si-conditii/" target="_blank" rel="noopener noreferrer">Conditions</a>{" "}
          and{" "}
          <a href="https://sailarmada.com/privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} style={{ color: "black" }} disabled={isSendingRequest}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={reserveBoat}
          disabled={isSendingRequest}
        >
          {isSendingRequest ? "Sending…" : "Send Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReservationModal;
